import { holdKeyboardForMenu } from './keys';
import { createContext, useContext, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { PopoverStack } from './popoverStack';
import { SheetGrabber, useSheetPull } from './phoneSheet';
import { usePhone } from './usePhone';

const PopoverDepth = createContext(0);
const dismissals = new PopoverStack();

/**
 * A popover: a small piece of glass that grows out of the control that asked for it.
 *
 * Drawn at the top of the document and placed against its anchor's box, so it is never
 * clipped by the panel the anchor lives in — the side panel scrolls, and a colour picker
 * cut off at the panel's edge is a colour picker you cannot use. It keeps itself inside
 * the window, follows the anchor if the layout moves under it, and closes on Escape or a
 * press anywhere else (listened for in the capture phase: the canvas swallows presses,
 * and a popover that the canvas could keep open is a popover that never goes away).
 *
 * Pressing the anchor itself does not count as "anywhere else": the anchor's own click
 * is what toggles it, and closing on the press would reopen it on the click.
 */

export type Placement = 'below' | 'above' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';

export interface PopoverProps {
  readonly open: boolean;
  readonly anchor: HTMLElement | null;
  readonly onClose: () => void;
  /** Position beside a containing panel while retaining the trigger for dismissal. */
  readonly positionAnchor?: HTMLElement | null;
  readonly placement?: Placement;
  readonly align?: Align;
  /** Gap between the anchor and the glass. */
  readonly offset?: number;
  readonly width?: number;
  readonly className?: string;
  /** Label for assistive technology: what this popover is. */
  readonly label?: string;
  readonly role?: string;
  /**
   * While open, keys go nowhere else — as a menu's do. For pickers whose own parts cannot
   * all take focus (a colour square), where Delete and the arrows otherwise reached the
   * shape behind them (QA 2026-09-24, paint-F2).
   */
  readonly ownsKeyboard?: boolean;
  readonly children: ReactNode;
  /** Presses inside elements matching this selector never close it. */
  readonly keepOpenWithin?: string;
  /**
   * The press that closes it does nothing else, as with a Mac context menu. A context
   * menu's dismissing click used to reach the canvas too: it changed the selection, and a
   * secondary press (a trackpad's two-finger tap) opened the Canvas menu in its place.
   */
  readonly swallowDismissal?: boolean;
}

const MARGIN = 8;

export function Popover({
  open,
  anchor,
  onClose,
  positionAnchor,
  placement = 'below',
  align = 'start',
  offset = 8,
  width,
  className = '',
  label,
  role = 'dialog',
  ownsKeyboard = false,
  children,
  keepOpenWithin,
  swallowDismissal = false,
}: PopoverProps) {
  const depth = useContext(PopoverDepth);
  const layer = useRef(Symbol('popover'));
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number; origin: string; zIndex: number } | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  /**
   * On a phone every popover is a bottom sheet (Artwork A, 2026-10-26): a 330 px picker hung off a
   * control ran past a 320 px screen, and a submenu flying out to the right had nowhere to go.
   */
  const phone = usePhone();
  const pull = useSheetPull(onClose);

  const place = useCallback(() => {
    const element = ref.current;
    if (!element || !anchor || phone) return;
    const a = (positionAnchor ?? anchor).getBoundingClientRect();
    const w = element.offsetWidth;
    const h = element.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let side = placement;
    // Flip to the other side when there is no room on the one asked for.
    if (side === 'below' && a.bottom + offset + h > vh - MARGIN && a.top - offset - h > MARGIN) side = 'above';
    else if (side === 'above' && a.top - offset - h < MARGIN && a.bottom + offset + h < vh - MARGIN) side = 'below';
    else if (side === 'left' && a.left - offset - w < MARGIN && a.right + offset + w < vw - MARGIN) side = 'right';
    else if (side === 'right' && a.right + offset + w > vw - MARGIN && a.left - offset - w > MARGIN) side = 'left';

    let left: number;
    let top: number;
    if (side === 'below' || side === 'above') {
      top = side === 'below' ? a.bottom + offset : a.top - offset - h;
      left = align === 'start' ? a.left : align === 'end' ? a.right - w : a.left + a.width / 2 - w / 2;
    } else {
      left = side === 'right' ? a.right + offset : a.left - offset - w;
      top = align === 'start' ? a.top : align === 'end' ? a.bottom - h : a.top + a.height / 2 - h / 2;
    }
    left = Math.max(MARGIN, Math.min(vw - MARGIN - w, left));
    top = Math.max(MARGIN, Math.min(vh - MARGIN - h, top));
    // It grows from the edge nearest the anchor, which is what makes it read as coming
    // out of the control rather than appearing beside it.
    const ox = Math.max(0, Math.min(w, a.left + a.width / 2 - left));
    const oy = Math.max(0, Math.min(h, a.top + a.height / 2 - top));
    const origin = side === 'below' ? `${ox}px 0` : side === 'above' ? `${ox}px ${h}px` : side === 'right' ? `0 ${oy}px` : `${w}px ${oy}px`;
    // A picker in the AI bar or another raised surface must open above that surface.
    let zIndex = 50;
    for (let parent: HTMLElement | null = anchor; parent; parent = parent.parentElement) {
      const level = Number.parseInt(getComputedStyle(parent).zIndex, 10);
      if (Number.isFinite(level)) zIndex = Math.max(zIndex, level + 1);
    }
    setAt(was => was && was.left === left && was.top === top && was.origin === origin && was.zIndex === zIndex ? was : { left, top, origin, zIndex });
  }, [anchor, positionAnchor, placement, align, offset, phone]);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
  }, [open, place]);

  // Follow the anchor and the popover's own size while it is open.
  useEffect(() => {
    if (!open) return;
    const element = ref.current;
    const observer = new ResizeObserver(() => place());
    if (element) observer.observe(element);
    if (anchor) observer.observe(anchor);
    window.addEventListener('resize', place);
    /**
     * And the anchor's POSITION, which neither of those sees. A quick-bar menu or colour
     * picker stayed where it opened while a pan or a scroll carried the bar away with the
     * shape (QA 2026-09-24, menus-shortcuts-quickbar-F25). Checked once a frame while
     * open; placed again only when the anchor has actually moved.
     */
    let frame = 0;
    let last = '';
    const follow = () => {
      // A detached anchor measures as all zeros; the popover stays put rather than
      // jumping to the corner.
      if (anchor?.isConnected) {
        const a = (positionAnchor ?? anchor).getBoundingClientRect();
        const now = `${a.left},${a.top},${a.width},${a.height}`;
        if (last && now !== last) place();
        last = now;
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      cancelAnimationFrame(frame);
    };
  }, [open, anchor, positionAnchor, place]);

  // A menu owns the keyboard while it is open, so its keys never reach the page behind it.
  useEffect(() => {
    if (!open || (role !== 'menu' && !ownsKeyboard)) return;
    return holdKeyboardForMenu();
  }, [open, role, ownsKeyboard]);

  useEffect(() => {
    if (!open) return;
    const removeLayer = dismissals.add(layer.current, depth);
    const onDown = (event: PointerEvent) => {
      if (!dismissals.claim(layer.current, event)) return;
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      // Something on the page that works WITH the open popover: the canvas while a
      // gradient's handles are on it, which the picker would otherwise close under the hand
      // reaching for them. Whoever sets the flag is the one who knows it is safe.
      if (target instanceof Element && target.closest('[data-keeps-popover="true"]')) return;
      if (keepOpenWithin && (target as HTMLElement).closest?.(keepOpenWithin)) return;
      // A press on the menu a submenu came from still belongs to that menu.
      if (swallowDismissal && !(target instanceof Element && target.closest('[role="menu"]'))) swallowPress(event);
      closeRef.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissals.claim(layer.current, event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeRef.current();
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      removeLayer();
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, anchor, keepOpenWithin, depth, swallowDismissal]);

  if (!open) return null;
  if (phone) {
    return createPortal(
      <div
        ref={(element) => {
          ref.current = element;
          pull.sheet.current = element;
        }}
        role={role}
        aria-label={label}
        data-popover-child
        className={`phone-popover-sheet glass-thick ${className}`}
        // Each one opened from inside another stands above it, and every sheet above the drawers'.
        style={{ zIndex: 60 + depth }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <SheetGrabber {...pull.grab} />
        <div className="phone-sheet-body ui-scroll">
          <PopoverDepth.Provider value={depth + 1}>{children}</PopoverDepth.Provider>
        </div>
      </div>,
      anchor?.closest('dialog[open]') ?? document.body,
    );
  }
  return createPortal(
    <div
      ref={ref}
      role={role}
      aria-label={label}
      data-popover-child
      className={`glass-thick ui-scroll fixed z-50 max-h-[calc(100vh-16px)] rounded-[16px] ${at ? 'ui-pop' : 'pointer-events-none opacity-0'} ${className}`}
      style={{ left: at?.left ?? 0, top: at?.top ?? 0, width, transformOrigin: at?.origin, zIndex: at?.zIndex }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <PopoverDepth.Provider value={depth + 1}>{children}</PopoverDepth.Provider>
    </div>,
    // Native modal dialogs make the rest of the document inert. Their menus belong
    // inside the dialog's top layer, where they remain visible and interactive.
    anchor?.closest('dialog[open]') ?? document.body,
  );
}

/**
 * Keep one press from reaching the page: its pointerdown, and the mouse, click and
 * contextmenu events the browser sends after it. Cancelling the pointerdown already stops
 * mousedown, mouseup and focus; click and contextmenu still come, so they are caught once.
 */
function swallowPress(event: PointerEvent) {
  event.preventDefault();
  event.stopPropagation();
  const block = (later: Event) => {
    later.preventDefault();
    later.stopPropagation();
  };
  const stop = () => {
    for (const type of ['click', 'auxclick', 'contextmenu'] as const) window.removeEventListener(type, block, true);
    window.removeEventListener('pointerup', settle, true);
  };
  // The click and contextmenu of this press arrive by its pointerup (contextmenu, on a
  // Mac, right after the pointerdown). A frame later they are someone else's.
  const settle = () => requestAnimationFrame(() => setTimeout(stop, 0));
  for (const type of ['click', 'auxclick', 'contextmenu'] as const) window.addEventListener(type, block, true);
  window.addEventListener('pointerup', settle, true);
  // A press that never ends here (the window lost focus) still lets go.
  setTimeout(stop, 2000);
}

/**
 * The element a popover hangs from, kept in state rather than a ref: a popover placed
 * against a ref reads it before the ref is set and opens in the corner.
 */
export function useAnchor<T extends HTMLElement = HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);
  return [element, setElement] as const;
}
